/**
 * Notion Connection - Zod Schemas
 *
 * Validation schemas for Notion page/database browsing and upload operations.
 */

import { z } from "zod";

// ═══════════════════════════════════════════════════════════════
// REQUEST SCHEMAS
// ═══════════════════════════════════════════════════════════════

/**
 * Notion Auth Callback - Request body
 */
export const DocumentNotionAuthBodySchema = z.object({
  code: z.string().min(1, "Authorization code is required"),
  redirectUri: z.string().url("Invalid redirect URI")
});

/**
 * Search Notion - Query params (GET request)
 */
export const DocumentSearchNotionParamsSchema = z.object({
  connectionId: z.string().uuid(),
  query: z.string().optional(),
  filter: z.enum(["page", "database"]).optional(),
  cursor: z.string().optional()
});

/**
 * Upload Notion Content - Request body
 */
export const DocumentUploadNotionContentBodySchema = z.object({
  connectionId: z.string().uuid(),
  workspaceId: z.string().optional(),
  pageIds: z.array(z.string()).optional(),
  databaseIds: z.array(z.string()).optional(),
  parentId: z.string().optional()
});

// ═══════════════════════════════════════════════════════════════
// RESPONSE SCHEMAS
// ═══════════════════════════════════════════════════════════════

/**
 * Notion Auth Response
 */
export const DocumentNotionAuthResponseSchema = z.object({
  accessToken: z.string(),
  workspaceId: z.string().optional(),
  workspaceName: z.string().optional(),
  workspaceIcon: z.string().optional(),
  botId: z.string().optional()
});

/**
 * Notion Item (Page or Database)
 */
export const NotionItemSchema = z.object({
  id: z.string(),
  object: z.enum(["page", "database"]),
  title: z.string(),
  url: z.string(),
  createdTime: z.string(),
  lastEditedTime: z.string(),
  icon: z.string().optional()
});

/**
 * Search Notion Response
 */
export const DocumentSearchNotionResponseSchema = z.object({
  items: z.array(NotionItemSchema),
  hasMore: z.boolean(),
  nextCursor: z.string().nullish() // Backend returns null when no more pages
});

/**
 * Upload Notion Content Response
 */
export const DocumentUploadNotionContentResponseSchema = z.object({
  message: z.string(),
  documentIds: z.array(z.string()).optional()
});
